class CartImagePolice {
  constructor() {
    this.queue = new Map();
    this.intervalId = null;
    this.POLL_INTERVAL_MS = 1500;
    this.isUpdatingBatch = false;
    this.abortController = null;
    this.isPaused = false;
  }

  /**
   * Call this right before a removal request is sent, so the poller can't
   * race a concurrent /cart/change.js call against a shrinking/reordering
   * cart.items array. Pair with handleCartItemRemoval() once the removal
   * request settles to resume.
   */
  pause() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.stopPolling();
    this.isUpdatingBatch = false;
    this.isPaused = true;
  }

  async init() {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();
      this.hydrateFromCart(cart);
    } catch (err) {
      console.error("Failed to load cart for image police:", err);
    }
  }

  /**
   * Call this when items are removed from the cart.
   * Cancels in-flight network requests, resets timers, and rebuilds the queue.
   */
  handleCartItemRemoval(newCart) {
    // 1. Cancel any active image HEAD/GET checks in flight
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // 2. Stop ongoing polling interval
    this.stopPolling();

    // 3. Reset state flags
    this.isUpdatingBatch = false;
    this.isPaused = false;

    // 4. Rehydrate queue with new cart data (or fetch if not provided)
    if (newCart) {
      this.hydrateFromCart(newCart);
    } else {
      this.init();
    }
  }

  // Alias for alternative naming preferences
  clearQueueAndRehydrate(newCart) {
    this.handleCartItemRemoval(newCart);
  }

  hydrateFromCart(cart) {
    if (!cart?.items?.length) {
      this.queue.clear();
      this.stopPolling();
      return;
    }

    setTimeout(() => {
      const cartLinks = document.querySelectorAll('[href="/cart"]');
      cartLinks.forEach((link) => {
        link.style.opacity = "0.5";
        link.style.pointerEvents = "none";
      });
      setTimeout(() => {
        cartLinks.forEach((link) => {
          link.style.opacity = "";
          link.style.pointerEvents = "";
        });
      }, 1000);
    }, 500);

    this.queue.clear();

    cart.items.forEach((item) => {
      const props = item.properties || {};
      const status = props["_imageStatus"];
      const imageUrl = props["_plateImage"];

      if (status === "pending" && imageUrl) {
        this.queue.set(item.key, {
          key: item.key,
          imageUrl,
          properties: { ...props },
          quantity: item.quantity,
        });
      }
    });

    if (this.queue.size > 0 && !this.intervalId) {
      this.startPolling();
    } else if (this.queue.size === 0 && this.intervalId) {
      this.stopPolling();
    }
  }

  async checkImageReady(url, signal) {
    const bustUrl = `${url}?v=${Date.now()}`;
    try {
      const response = await fetch(bustUrl, {
        method: "GET",
        cache: "no-cache",
        signal,
      });
      if (!response.ok) return false;
      const contentType = response.headers.get("content-type") || "";
      return contentType.startsWith("image/");
    } catch (err) {
      if (err.name === "AbortError") return false;

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = bustUrl;
      });
    }
  }

  async processReadyBatch(readyItems, signal) {
    if (this.isPaused) return;
    if (this.isUpdatingBatch || readyItems.length === 0) return;
    this.isUpdatingBatch = true;

    try {
      // 1. Fetch fresh cart state to verify items still exist
      const freshCartRes = await fetch(`${Shopify.routes.root}cart.js`, {
        signal,
      });
      const freshCart = await freshCartRes.json();
      const activeKeys = new Set(freshCart.items.map((i) => i.key));

      const validItemsToUpdate = readyItems.filter((item) =>
        activeKeys.has(item.key),
      );

      if (validItemsToUpdate.length === 0) {
        readyItems.forEach((item) => this.queue.delete(item.key));
        this.isUpdatingBatch = false;
        return;
      }

      let latestCart = freshCart;

      // 2. Loop through valid ready items and update properties via /cart/change.js
      for (const readyItem of validItemsToUpdate) {
        if (signal?.aborted) break;

        // Find the current line index (1-based) in case position changed
        const currentLineIndex =
          latestCart.items.findIndex((i) => i.key === readyItem.key) + 1;
        if (currentLineIndex === 0) continue; // Item was removed in a previous step

        const currentProperties =
          latestCart.items[currentLineIndex - 1].properties || {};
        const updatedProperties = {
          ...currentProperties,
          _imageStatus: "ready",
        };

        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            line: currentLineIndex,
            properties: updatedProperties,
          }),
          signal,
        });

        if (response.ok) {
          latestCart = await response.json();
          this.syncLineKey(readyItem.key, updatedProperties, latestCart);
          this.queue.delete(readyItem.key);
        }
      }

      // 3. Dispatch final updated cart event once processing finishes
      window.dispatchEvent(
        new CustomEvent("cart:police-updated", { detail: latestCart }),
      );
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(
          "Failed to process cart image update via /cart/change.js:",
          err,
        );
      }
    } finally {
      this.isUpdatingBatch = false;
    }
  }

  syncLineKey(oldKey, updatedProperties, updatedCart) {
    const sortedProps = JSON.stringify(
      updatedProperties,
      Object.keys(updatedProperties).sort(),
    );
    const newItem = updatedCart.items.find(
      (i) =>
        JSON.stringify(i.properties, Object.keys(i.properties).sort()) ===
        sortedProps,
    );
    if (!newItem || newItem.key === oldKey) return;

    document.querySelectorAll(`[data-line-key="${oldKey}"]`).forEach((el) => {
      el.setAttribute("data-line-key", newItem.key);
    });
    document.querySelectorAll("[data-line-keys]").forEach((el) => {
      const keys = el.getAttribute("data-line-keys").split(",");
      if (!keys.includes(oldKey)) return;
      el.setAttribute(
        "data-line-keys",
        keys.map((k) => (k === oldKey ? newItem.key : k)).join(","),
      );
    });
  }

  startPolling() {
    const runPoll = async () => {
      if (this.isPaused) return;
      if (this.queue.size === 0 || this.isUpdatingBatch) {
        if (this.queue.size === 0) this.stopPolling();
        return;
      }

      // Create new AbortController instance for this cycle
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      const pendingItems = Array.from(this.queue.values());

      try {
        const checkResults = await Promise.all(
          pendingItems.map(async (item) => {
            const isReady = await this.checkImageReady(item.imageUrl, signal);
            return isReady ? item : null;
          }),
        );

        const readyItems = checkResults.filter(Boolean);
        if (readyItems.length > 0 && !signal.aborted) {
          await this.processReadyBatch(readyItems, signal);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Polling error:", err);
        }
      }
    };

    runPoll();
    this.intervalId = setInterval(runPoll, this.POLL_INTERVAL_MS);
  }

  stopPolling() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

window.CartPolice = new CartImagePolice();
window.CartPolice.init();
